import React, { useState } from 'react';
import axiosInstance from '../config/axios';
import { ChevronDown, ChevronUp, Trash2, Eye, EyeOff, Edit3 } from 'lucide-react';
import './MyCounts.css';
import { friendlyBatchItemLabel } from '../utils/inventoryDisplay';
import { getFriendlyErrorMessage } from '../utils/errorMessages';

const formatQuantityTo2Display = (val) => {
  if (val === null || val === undefined || val === '') return '-';
  const n = parseFloat(String(val).replace(',', '.'));
  return isNaN(n) ? '-' : Number(n).toFixed(2);
};

/** Site my-counts API used category/unit before pivotCategory/unit1 were added */
const batchItemCategory = (item) => item.pivotCategory ?? item.category ?? '';
const batchItemUnit1 = (item) => item.unit1 ?? item.unit ?? '';
const batchItemPackSize = (item) => item.packSize ?? '';

const MyCounts = ({ counts: batches, onRefresh, onEditRecalled }) => {
  const [deleting, setDeleting] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedBatches, setExpandedBatches] = useState({});

  const handleDelete = async (batchId) => {
    if (!window.confirm('Are you sure you want to recall this entire count? All items for this site will be recalled and you will be able to resubmit after making changes.')) {
      return;
    }
    setDeleting(prev => ({ ...prev, [batchId]: true }));
    setError('');
    setSuccess('');
    try {
      const endpoint = `/api/tenant/site/counts/batch/${batchId}`;
      const response = await axiosInstance.delete(endpoint);
      const successMessage = response.data.recalledCount 
        ? `Count recalled successfully (${response.data.recalledCount} items). You can now edit and resubmit it.`
        : response.data.deletedCount
        ? `Count deleted successfully (${response.data.deletedCount} items).`
        : response.data.message || 'Count recalled successfully.';
      setSuccess(successMessage);
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      const friendly = getFriendlyErrorMessage(error, { kind: 'site_recall' });
      const errorMessage =
        friendly ||
        error.response?.data?.userMessage ||
        error.response?.data?.error ||
        'Failed to recall count';
      const errorDetails =
        error.response?.data?.code === 'COUNT_REVIEW_ALREADY_STARTED'
          ? ''
          : error.response?.data?.details || '';
      console.error('Recall error:', error.response?.data);
      setError(errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);
      if (error.response?.data?.code === 'COUNT_REVIEW_ALREADY_STARTED' && onRefresh) {
        onRefresh();
      }
      setTimeout(() => setError(''), 8000);
    } finally {
      setDeleting(prev => ({ ...prev, [batchId]: false }));
    }
  };

  const toggleExpand = (batchId) => {
    setExpandedBatches(prev => ({ ...prev, [batchId]: !prev[batchId] }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { cls: 'mc-badge-amber', text: 'Pending Review' },
      completed: { cls: 'mc-badge-emerald', text: 'Review Completed' },
      approved: { cls: 'mc-badge-emerald', text: 'Approved' },
      rejected: { cls: 'mc-badge-rose', text: 'Rejected' },
      recalled: { cls: 'mc-badge-indigo', text: 'Recalled - Can Edit' },
      mixed: { cls: 'mc-badge-slate', text: 'Mixed Status' }
    };
    const badge = badges[status] || badges.pending;
    return <span className={`mc-badge ${badge.cls}`}>{badge.text}</span>;
  };

  const batchIsRecallLocked = (batch) =>
    !!(batch.recallLocked || batch.managerReviewStarted);

  const pendingBatches = batches.filter(b => b.status === 'pending');
  const completedBatches = batches.filter(b => b.status === 'completed');
  const recalledBatches = batches.filter(b => b.status === 'recalled');
  const rejectedBatches = batches.filter(b => b.status === 'rejected');
  const reviewedBatches = batches.filter(b => b.status !== 'pending' && b.status !== 'completed' && b.status !== 'recalled' && b.status !== 'rejected');

  const renderBatchCard = (batch, options = {}) => {
    const { showRecall = false, showEdit = false, borderClass = '' } = options;
    const isExpanded = expandedBatches[batch.batchId];
    const recallLocked = showRecall && batchIsRecallLocked(batch);
    return (
      <div key={batch.batchId} className={`mc-batch-card ${borderClass}`}>
        <div className="mc-batch-header">
          <div className="mc-batch-info">
            <span className="mc-batch-site">{batch.siteName}</span>
            <span className="mc-batch-meta">{batch.itemCount} {batch.itemCount === 1 ? 'item' : 'items'}</span>
          </div>
          <div className="mc-batch-actions">
            {getStatusBadge(batch.status)}
            {showRecall && !recallLocked && (
              <button className="mc-icon-btn mc-icon-danger" onClick={() => handleDelete(batch.batchId)} disabled={deleting[batch.batchId]} title="Recall this entire count">
                <Trash2 size={14} /> {deleting[batch.batchId] ? 'Recalling...' : 'Recall'}
              </button>
            )}
            {showEdit && onEditRecalled && (
              <button className="mc-icon-btn mc-icon-edit" onClick={() => onEditRecalled(batch.siteId)} title="Edit and resubmit">
                <Edit3 size={14} /> Edit
              </button>
            )}
          </div>
        </div>
        {recallLocked && (
          <div className="mc-batch-locked-msg">
            This count is now under manager review and can no longer be recalled.
          </div>
        )}
        <div className="mc-batch-detail">
          <span>Counted by: {batch.personName || '-'}</span>
          <span>{batch.countDate ? new Date(batch.countDate).toLocaleString() : new Date(batch.createdAt).toLocaleString()}</span>
        </div>
        <button className="mc-expand-btn" onClick={() => toggleExpand(batch.batchId)}>
          {isExpanded ? <><EyeOff size={14} /> Hide Items</> : <><Eye size={14} /> Show Items</>}
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {isExpanded && (
          <div className="mc-items-wrap">
            <table className="mc-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Category</th>
                  <th>Pack Size</th>
                  <th>Unit 1</th>
                  <th>Counted</th>
                  <th>Unit 2</th>
                  <th>Counted 2</th>
                  <th>Location</th>
                  <th>Priority</th>
                  <th>Notes</th>
                  <th>Alert</th>
                </tr>
              </thead>
              <tbody>
                {(batch.items || []).map((item, index) => (
                  <tr key={item.id || index}>
                    <td className="mc-item-name">{friendlyBatchItemLabel(item)}</td>
                    <td><span className="cs-category-pill">{batchItemCategory(item) || '-'}</span></td>
                    <td className="mc-pack-size-cell">{batchItemPackSize(item) || ''}</td>
                    <td>{batchItemUnit1(item) || '-'}</td>
                    <td>{formatQuantityTo2Display(item.countedQuantity)}</td>
                    <td>{item.unit2 || '-'}</td>
                    <td>{formatQuantityTo2Display(item.countedQuantity2)}</td>
                    <td>{item.location || '-'}</td>
                    <td style={{ textAlign: 'center' }}>{item.priority ? (<span className="cs-priority-pill">Priority</span>) : '-'}</td>
                    <td>{item.notes || '-'}</td>
                    <td style={{ textAlign: 'center' }}>{item.isHighValue ? (<span className="cs-high-value-pill" title="Total value exceeds admin threshold">High Value</span>) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Mobile card view for items */}
            <div className="mc-mobile-items">
              {(batch.items || []).map((item, index) => (
                <div key={item.id || index} className="mc-mobile-item">
                  <div className="mc-mobile-item-header">
                    <div className="mc-mobile-item-header-main">
                      <span className="mc-mobile-item-name">{friendlyBatchItemLabel(item)}</span>
                      {batchItemPackSize(item) ? (
                        <div className="mc-mobile-item-pack">{batchItemPackSize(item)}</div>
                      ) : null}
                      <div className="mc-mobile-item-meta">
                        <span className="cs-category-pill">{batchItemCategory(item) || '-'}</span>
                      </div>
                    </div>
                    <div className="mc-mobile-item-badges">
                      {item.priority && <span className="cs-priority-pill">Priority</span>}
                    </div>
                  </div>
                  <div className="mc-mobile-item-row">
                    <div className="mc-mobile-item-field"><span className="mc-mobile-item-label">{batchItemUnit1(item) || 'Unit 1'}</span><span className="mc-mobile-item-value">{formatQuantityTo2Display(item.countedQuantity)}</span></div>
                    {item.unit2 && <div className="mc-mobile-item-field"><span className="mc-mobile-item-label">{item.unit2}</span><span className="mc-mobile-item-value">{formatQuantityTo2Display(item.countedQuantity2)}</span></div>}
                  </div>
                  {item.location && <div className="mc-mobile-item-location">Location: {item.location}</div>}
                  {item.notes && <div className="mc-mobile-item-notes">Notes: {item.notes}</div>}
                  {item.isHighValue && <div className="mc-mobile-item-alert">High Value - Please re check count</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mc-section">
      <h3 className="mc-title">My Submitted Counts</h3>
      
      {error && <div className="mc-msg mc-msg-error">{error}</div>}
      {success && <div className="mc-msg mc-msg-success">{success}</div>}

      {batches.length === 0 ? (
        <div className="mc-empty">You haven't submitted any counts yet.</div>
      ) : (
        <>
          {pendingBatches.length > 0 && (
            <div className="mc-group">
              <div className="mc-group-header mc-group-amber">
                <h4 className="mc-group-title">Pending Review ({pendingBatches.length})</h4>
                <p className="mc-group-desc">You can recall entire counts to make changes before they are reviewed.</p>
              </div>
              {pendingBatches.map(batch => renderBatchCard(batch, { showRecall: true }))}
            </div>
          )}

          {recalledBatches.length > 0 && (
            <div className="mc-group">
              <div className="mc-group-header mc-group-rose">
                <h4 className="mc-group-title">Recalled Counts ({recalledBatches.length})</h4>
                <p className="mc-group-desc">These counts have been recalled. You can edit and resubmit them from the "Submit Count" tab.</p>
              </div>
              {recalledBatches.map(batch => renderBatchCard(batch, { showEdit: true, borderClass: 'mc-batch-rose' }))}
            </div>
          )}

          {rejectedBatches.length > 0 && (
            <div className="mc-group">
              <div className="mc-group-header mc-group-rose">
                <h4 className="mc-group-title">Rejected Counts ({rejectedBatches.length})</h4>
                <p className="mc-group-desc">These counts have been rejected by your manager. Please edit and resubmit.</p>
              </div>
              {rejectedBatches.map(batch => renderBatchCard(batch, { showEdit: true, borderClass: 'mc-batch-rose' }))}
            </div>
          )}

          {completedBatches.length > 0 && (
            <div className="mc-group">
              <div className="mc-group-header mc-group-emerald">
                <h4 className="mc-group-title">Review Completed ({completedBatches.length})</h4>
                <p className="mc-group-desc">Your manager has completed the review of these counts. Wait for a new counting period to start before submitting new counts.</p>
              </div>
              {completedBatches.map(batch => renderBatchCard(batch))}
            </div>
          )}

          {reviewedBatches.length > 0 && (
            <div className="mc-group">
              <div className="mc-group-header">
                <h4 className="mc-group-title">Reviewed Counts ({reviewedBatches.length})</h4>
              </div>
              {reviewedBatches.map(batch => renderBatchCard(batch))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyCounts;